import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { __, CardComponent, MessengerService, ModalHistoryComponent } from '@web/birdz-angular';
import { ModalConfirmDeleteQueryComponent } from 'app/components/modal-confirm-delete-query/modal-confirm-delete-query.component';
import { ModalIconPickerComponent } from 'app/components/modal-icon-picker/modal-icon-picker.component';
import { ModalInsertTemplateFieldComponent } from 'app/components/modal-insert-template-field/modal-insert-template-field.component';
import { ModalQueryHelpComponent } from 'app/components/modal-query-help/modal-query-help.component';
import { Database } from 'app/interfaces/database.interface';
import { Query } from 'app/interfaces/query.interface';
import { QueryService } from 'app/services/query.service';
import { UserService } from 'app/services/user.service';
import { buildEnvTag } from 'app/utils/template-parser.utils';
import { selectOnlyValidator } from 'app/validators/sql.validators';
import { environment } from 'environments/environment';
import { map, switchMap, tap } from 'rxjs';

@Component({
  selector: 'app-page-query-edit',
  templateUrl: './page-query-edit.component.html',
  styleUrls: ['./page-query-edit.component.scss'],
  imports: [
    CardComponent,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslocoPipe,
  ],
})
export class PageQueryEditComponent implements OnInit {
  @ViewChild('sqlTextarea') sqlTextarea!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('tagInput') tagInput!: ElementRef<HTMLInputElement>;
  @ViewChild('colorMenuTrigger') colorMenuTrigger!: MatMenuTrigger;

  private queryService = inject(QueryService);
  private userService = inject(UserService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);
  private messenger = inject(MessengerService);
  private destroyRef = inject(DestroyRef);

  readonly colorPalette: { key: string; label: string; bg: string; fg: string }[] = [
    { key: 'accent-green', label: 'Vert', fg: '#e6f4ea', bg: '#1e8e3e' },
    { key: 'accent-teal', label: 'Teal', fg: '#e4f7fb', bg: '#0097a7' },
    { key: 'accent-blue', label: 'Bleu', fg: '#e8f0fe', bg: '#1a73e8' },
    { key: 'accent-indigo', label: 'Indigo', fg: '#e8eaf6', bg: '#3949ab' },
    { key: 'accent-purple', label: 'Violet', fg: '#f3e8fd', bg: '#9334ea' },
    { key: 'accent-pink', label: 'Rose', fg: '#fce4ec', bg: '#c2185b' },
    { key: 'accent-red', label: 'Rouge', fg: '#fce8e6', bg: '#d93025' },
    { key: 'accent-orange', label: 'Orange', fg: '#fbe9e7', bg: '#e64a19' },
    { key: 'accent-amber', label: 'Ambre', fg: '#fef3e2', bg: '#e37400' },
    { key: 'accent-yellow', label: 'Jaune', fg: '#fefce8', bg: '#a16207' },
    { key: 'accent-lime', label: 'Citron', fg: '#f0fdf4', bg: '#4d7c0f' },
    { key: 'accent-slate', label: 'Ardoise', fg: '#f1f5f9', bg: '#475569' },
  ];

  separatorKeysCodes = [ENTER, COMMA];
  form!: FormGroup;
  databases: Database[] = [];
  availableTags: string[] = [];
  selectedTags: string[] = [];
  queryId: string | null = null;
  isEditMode = false;
  isLoading = false;
  isSaving = false;
  isRestoring = false;
  currentUserEmail = '';
  isCreator = false;
  isEditor = false;
  isDeleting = false;
  query: Query | null = null;
  bigqueryInfoDismissed = false;

  get isDeleted(): boolean {
    return !!this.query?.deleted_at;
  }

  ngOnInit(): void {
    const user = this.userService.getUser();
    this.currentUserEmail = user?.getEmail() ?? '';
    this.isEditor = user?.can('query_editor') ?? false;
    this.initForm();

    this.queryService.getDatabases().subscribe((d) => (this.databases = d));
    this.queryService.getTags().subscribe((t) => (this.availableTags = t));

    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.queryId = params['id'] ?? null;
      this.isEditMode = !!this.queryId;
      this.isCreator = !this.isEditMode;

      if (this.isEditMode) {
        this.loadQuery();
      }
    });
  }

  private initForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      icon: [null as string | null],
      color: ['accent-green'],
      description: [''],
      sql: ['', [Validators.required, selectOnlyValidator]],
      database_id: ['bigquery', Validators.required],
      auto_execute: [false],
      is_locked: [false],
      is_private: [false],
    });
  }

  private loadQuery(): void {
    this.isLoading = true;
    this.queryService.getQuery(this.queryId!).subscribe({
      next: (query) => {
        this.isLoading = false;
        this.query = query;
        this.isCreator = query.creator_email === this.currentUserEmail;

        if (query.is_locked && !this.isCreator && !query.deleted_at) {
          this.router.navigate(['/query', this.queryId]);
          return;
        }

        this.selectedTags = query.tags ?? [];
        this.form.patchValue({
          name: query.name,
          icon: query.icon ?? null,
          color: query.color ?? 'accent-green',
          description: query.description,
          sql: query.sql,
          database_id: query.database_id,
          auto_execute: query.auto_execute,
          is_locked: query.is_locked,
          is_private: !query.is_public,
        });
      },
      error: () => {
        this.isLoading = false;
        this.router.navigate(['/queries']);
      },
    });
  }

  deleteQuery(): void {
    if (!this.query) {
      return;
    }
    this.dialog
      .open(ModalConfirmDeleteQueryComponent, { data: { name: this.query.name }, width: '400px' })
      .afterClosed()
      .pipe(
        switchMap((confirmed: boolean) => {
          if (!confirmed) {
            return [];
          }
          this.isDeleting = true;
          return this.queryService.deleteQuery(this.query!.id).pipe(
            tap(() => {
              this.messenger.success(__('QUERY_DELETED'));
              this.router.navigate(['/queries']);
            })
          );
        })
      )
      .subscribe({
        error: () => {
          this.isDeleting = false;
          this.messenger.error(__('ERROR_OCCURRED'));
        },
      });
  }

  restore(): void {
    if (!this.queryId) {
      return;
    }
    this.isRestoring = true;
    this.queryService.restoreQuery(this.queryId).subscribe({
      next: (query) => {
        this.isRestoring = false;
        this.query = query;
        this.messenger.success(__('QUERY_RESTORED'));
        this.router.navigate(['/query', query.id]);
      },
      error: () => {
        this.isRestoring = false;
        this.messenger.error(__('ERROR_OCCURRED'));
      },
    });
  }

  addTag(value: string): void {
    const tag = value.trim();
    if (tag && !this.selectedTags.includes(tag)) {
      this.selectedTags = [...this.selectedTags, tag];
    }
    if (this.tagInput) {
      this.tagInput.nativeElement.value = '';
    }
  }

  removeTag(tag: string): void {
    this.selectedTags = this.selectedTags.filter((t) => t !== tag);
  }

  onTagSelected(event: MatAutocompleteSelectedEvent): void {
    this.addTag(event.option.viewValue);
  }

  get currentIcon(): string | null {
    return this.form.get('icon')?.value ?? null;
  }

  get currentColor(): string {
    return this.form.get('color')?.value ?? 'accent-green';
  }

  get currentColorBg(): string {
    return this.colorPalette.find((c) => c.key === this.currentColor)?.bg ?? '#e6f4ea';
  }

  get currentColorFg(): string {
    return this.colorPalette.find((c) => c.key === this.currentColor)?.fg ?? '#1e8e3e';
  }

  selectColor(key: string): void {
    this.form.get('color')!.setValue(key);
    this.colorMenuTrigger.closeMenu();
  }

  get bigqueryServiceAccount(): string {
    const env = environment.env === 'local' ? 'dev' : environment.env;
    return `sac-${env}-que-querytools-gae@bdz-dtp-querytools-${env}.iam.gserviceaccount.com`;
  }

  openHelp(): void {
    this.dialog.open(ModalQueryHelpComponent, { width: '960px', maxHeight: '85vh' });
  }

  openIconPicker(): void {
    this.dialog
      .open(ModalIconPickerComponent, {
        data: { current: this.currentIcon },
        width: '720px',
        maxHeight: '85vh',
      })
      .afterClosed()
      .subscribe((result?: { selected: string | null }) => {
        if (result === undefined) {
          return;
        }
        this.form.get('icon')!.setValue(result.selected);
      });
  }

  insertTemplateField(type: 'text' | 'number' | 'textlist' | 'numberlist' | 'link' | 'date'): void {
    this.dialog
      .open(ModalInsertTemplateFieldComponent, { data: { type }, width: '560px' })
      .afterClosed()
      .pipe(
        tap((tag: string | null) => {
          if (tag) {
            this.insertAtCursor(tag);
          }
        })
      )
      .subscribe();
  }

  insertEnvField(): void {
    this.insertAtCursor(buildEnvTag());
  }

  private insertAtCursor(text: string): void {
    const textarea = this.sqlTextarea?.nativeElement;
    if (!textarea) {
      const current = this.form.get('sql')!.value as string;
      this.form.get('sql')!.setValue(current + text);
      return;
    }
    const start = textarea.selectionStart ?? textarea.value.length;
    const end = textarea.selectionEnd ?? textarea.value.length;
    this.form.get('sql')!.setValue(textarea.value.substring(0, start) + text + textarea.value.substring(end));
    setTimeout(() => {
      textarea.selectionStart = start + text.length;
      textarea.selectionEnd = start + text.length;
      textarea.focus();
    });
  }

  save(): void {
    if (this.form.invalid) {
      return;
    }

    if (this.tagInput?.nativeElement.value) {
      this.addTag(this.tagInput.nativeElement.value);
    }

    const { is_private, ...formValue } = this.form.value;
    const payload = {
      ...formValue,
      tags: this.selectedTags,
      is_public: !is_private,
    };

    this.isSaving = true;
    const request$ = this.isEditMode
      ? this.queryService.updateQuery(this.queryId!, payload)
      : this.queryService.createQuery(payload);

    request$.subscribe({
      next: (query) => {
        this.isSaving = false;
        this.queryService.refreshTags();
        this.messenger.success(__('QUERY_SAVED'));
        this.router.navigate(['/query', query.id]);
      },
      error: (err: HttpErrorResponse) => {
        this.isSaving = false;
        // Required/maxLength are checked client-side, so a 422 on the name means it is already taken.
        if (err.status === 422 && err.error?.errors?.name) {
          const nameControl = this.form.get('name')!;
          nameControl.setErrors({ unique: true });
          nameControl.markAsTouched();
          this.messenger.error(__('QUERY_NAME_ALREADY_USED'));
          return;
        }
        this.messenger.error(__('ERROR_OCCURRED'));
      },
    });
  }

  openChangelog(): void {
    const actionLabels: Record<string, string> = {
      'query.create': __('QUERY_ACTION_CREATE'),
      'query.update': __('QUERY_ACTION_UPDATE'),
      'query.delete': __('QUERY_ACTION_DELETE'),
    };

    const source$ = this.queryService
      .getLogs(this.queryId!)
      .pipe(map((logs) => logs.map((e) => ({ ...e, action: actionLabels[e.action] ?? e.action }))));

    this.dialog.open(ModalHistoryComponent, {
      data: {
        title: __('QUERY_CHANGELOG_BUTTON'),
        source$,
        columns: [
          { key: 'created_at', label: __('QUERY_HISTORY_DATE'), type: 'datetime' },
          { key: 'author', label: __('QUERY_HISTORY_USER'), type: 'string' },
          { key: 'action', label: __('QUERY_CHANGELOG_ACTION'), type: 'string' },
          { key: 'metadata', label: __('QUERY_CHANGELOG_CHANGES'), type: 'object' },
        ],
      },
      width: '860px',
      maxHeight: '80vh',
    });
  }

  openHistory(): void {
    const formatLabels: Record<string, string> = {
      html: __('QUERY_FORMAT_HTML'),
      csv: __('QUERY_FORMAT_CSV'),
      json: __('QUERY_FORMAT_JSON'),
    };

    const source$ = this.queryService.getExecutions(this.queryId!).pipe(
      map((executions) =>
        executions.map((e) => ({
          ...e,
          format: formatLabels[e.format] ?? e.format,
          duration_ms: e.duration_ms !== null ? `${e.duration_ms} ms` : null,
          bytes_processed: this.formatBytes(e.bytes_processed),
        }))
      )
    );

    this.dialog.open(ModalHistoryComponent, {
      data: {
        title: `${__('QUERY_HISTORY_BUTTON')} — ${this.form.get('name')?.value ?? ''}`,
        source$,
        columns: [
          { key: 'executed_at', label: __('QUERY_HISTORY_DATE'), type: 'datetime' },
          { key: 'executed_by', label: __('QUERY_HISTORY_USER'), type: 'string' },
          { key: 'format', label: __('QUERY_HISTORY_FORMAT'), type: 'string' },
          { key: 'duration_ms', label: __('QUERY_HISTORY_DURATION'), type: 'string' },
          { key: 'bytes_processed', label: __('QUERY_HISTORY_BYTES'), type: 'string' },
          { key: 'parameters', label: __('QUERY_HISTORY_PARAMETERS'), type: 'object' },
        ],
      },
      width: '860px',
      maxHeight: '80vh',
    });
  }

  private formatBytes(bytes: number | null): string {
    if (bytes === null) {
      return '—';
    }
    if (bytes === 0) {
      return '0 B';
    }
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    if (bytes < 1024 * 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }

  cancel(): void {
    if (this.isEditMode) {
      this.router.navigate(['/query', this.queryId]);
    } else {
      this.router.navigate(['/queries']);
    }
  }
}
